/**
 * FilterMenuButton — the funnel in the top strip. Dropdown of the four
 * filter types (Metrics / Label / Country / Rules) with the applied count next to
 * each, a blue dot on the funnel when anything is applied; picking an
 * item opens the filter popup on that tab (handoff screen 1).
 */

import { useEffect, useRef, useState } from "react";
import { I } from "@/components/Icon";
import { FILTER_TABS, TAB_LABEL, type FilterTab } from "@/lib/ads-manager-filter";
import { useLayer } from "@/lib/layer-stack";
import { useAdsFilter } from "./ads-filter/context";
import { DevicesIcon, TypeIcon } from "./ads-filter/filter-icons";
import { SPLIT_TITLE } from "@/lib/device-split";

// Keywords carry no labels (§17): the Label item has nothing to offer there.
const TABS_BY_GRAIN: Record<"campaigns" | "ad_groups" | "keywords", readonly FilterTab[]> = {
  campaigns: FILTER_TABS,
  ad_groups: FILTER_TABS,
  keywords: ["metrics", "country", "rules"],
};

export function FilterMenuButton({
  splitOnly = false,
  splitDisabledReason,
}: {
  /** Search terms / Ads tabs: only «Split by devices» applies there. */
  splitOnly?: boolean;
  /** The item is shown but can't be used here (Ads tab). */
  splitDisabledReason?: string;
} = {}) {
  const flt = useAdsFilter();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  useLayer(open, () => setOpen(false));
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);
  // On a tab where only the split applies, an app without an iPad build
  // has nothing to offer — no funnel at all.
  if (splitOnly && !flt.splitAvailable) return null;
  return (
    <div ref={wrapRef} className="af-funnel" style={{ display: "inline-block" }}>
      <button
        ref={btnRef}
        type="button"
        className="btn icon"
        title="Filters"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ position: "relative" }}
      >
        {I.filter}
        {(splitOnly ? flt.splitOn && !splitDisabledReason : flt.isActive || flt.splitOn) && (
          <span className="af-funnel-dot" />
        )}
      </button>
      {open && (
        <div className="af-menu" role="menu">
          <div className="af-menu-title">Filter by</div>
          {!splitOnly && TABS_BY_GRAIN[flt.grain].map((t) => (
            <button
              key={t}
              type="button"
              role="menuitem"
              className="af-menu-item"
              // Focus goes back to the funnel first: the menu item unmounts
              // with the menu, and the popup returns focus to whatever was
              // focused when it opened (layer stack).
              onClick={() => { setOpen(false); btnRef.current?.focus(); flt.openModal(t); }}
            >
              <TypeIcon tab={t} size={15} />
              {TAB_LABEL[t]}
              {flt.counts[t] > 0 && <span className="af-menu-count">{flt.counts[t]}</span>}
            </button>
          ))}
          {flt.splitAvailable && (
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={flt.splitOn}
              // aria-disabled, not `disabled`: a disabled button swallows
              // the hover in some browsers and the reason would never show.
              className={`af-menu-item${splitDisabledReason ? " af-menu-item-disabled" : ""}`}
              aria-disabled={!!splitDisabledReason}
              title={splitDisabledReason}
              onClick={() => {
                if (splitDisabledReason) return;
                setOpen(false); btnRef.current?.focus(); flt.setSplit(!flt.splitOn);
              }}
            >
              <DevicesIcon size={15} />
              {SPLIT_TITLE}
              {flt.splitOn && <span className="af-menu-count">On</span>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
